import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNetworkStatus } from './useNetworkStatus';
import { offlineQueueManager, OfflineOperation, OfflineQueue } from '@shared/lib/offlineQueue';
import { offlineDataManager } from '@shared/lib/offlineData';
import { api } from '@shared/services/api';
import { queryKeys } from '@shared/lib/queryKeys';
import { Collection } from '@shared/types';
import { toast } from '@shared/lib/toast';
import { storage } from '@shared/lib/storage';
import { isOnlineNow } from '@shared/lib/network';

const LAST_SYNC_KEY = 'hintword_last_sync_time';
const HISTORY_KEY = 'hintword_sync_history';
const HISTORY_LIMIT = 50;

export interface SyncHistoryEntry {
  time: string;        // ISO timestamp
  ok: boolean;         // pushed with no failures AND pulled successfully
  pushed: number;      // operations sent to the server
  failed: number;      // operations that errored (stay queued)
  pulled: boolean;     // server data fetched successfully
}

const readLastSync = (): Date | null => {
  try {
    const stored = storage.getItem(LAST_SYNC_KEY);
    return stored ? new Date(stored) : null;
  } catch {
    return null;
  }
};

const readHistory = (): SyncHistoryEntry[] => {
  try {
    const stored = storage.getItem(HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const writeHistory = (entry: SyncHistoryEntry): SyncHistoryEntry[] => {
  const next = [entry, ...readHistory()].slice(0, HISTORY_LIMIT);
  try {
    storage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {}
  return next;
};

// Offline-first sync. The queue and local storage are the source of truth; the
// network is only ever contacted from syncNow(), which the user triggers by
// clicking the Sync button. There is NO automatic/background syncing.
export const useOfflineSync = () => {
  const queryClient = useQueryClient();
  const { isOnline, isReconnecting } = useNetworkStatus();
  const [queue, setQueue] = useState<OfflineQueue>(offlineQueueManager.getQueueStatus());
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(readLastSync());
  const [syncHistory, setSyncHistory] = useState<SyncHistoryEntry[]>(readHistory());

  // Subscribe to queue changes
  useEffect(() => {
    const unsubscribe = offlineQueueManager.subscribe(setQueue);
    return unsubscribe;
  }, []);

  // Push a single queued operation to the server. Returns the server-assigned
  // id remap (if this op created a new record), or null.
  const pushOperation = useCallback(async (
    operation: OfflineOperation
  ): Promise<{ oldId: string; newId: string; kind: 'note' | 'collection' | 'task' | 'link' | 'tab' } | null> => {
    switch (operation.type) {
      case 'CREATE':
      case 'UPDATE': {
        if ('note_id' in operation.data) {
          const data: any = { ...operation.data };
          const localId = String(data.note_id || '');
          const isLocal = localId.startsWith('local-');
          if (isLocal) delete data.note_id;
          const saved = await api.createOrUpdateNote(data);
          if (isLocal && saved?.note_id) {
            return { oldId: localId, newId: saved.note_id, kind: 'note' };
          }
          return null;
        }
        // Tab create/update. Older queued creates carried no tab_id at all,
        // so also match on collection_id + url.
        if ('tab_id' in operation.data || ('collection_id' in operation.data && 'url' in operation.data)) {
          const data: any = { ...operation.data };
          const localId = String(data.tab_id || '');
          const isLocal = localId.startsWith('local-');
          if (isLocal) delete data.tab_id;
          const response = await api.createOrUpdateTab(data);
          const saved: any = response?.data;
          if (isLocal && saved?.tab_id) {
            return { oldId: localId, newId: saved.tab_id, kind: 'tab' };
          }
          return null;
        }
        if ('name' in operation.data && typeof operation.data.name === 'string') {
          await api.createCollection(operation.data as { name: string });
          return null;
        }
        return null;
      }
      case 'CREATE_COLLECTION': {
        const data: any = operation.data;
        if ('name' in data && typeof data.name === 'string') {
          const localId = data.collection_id ? String(data.collection_id) : '';
          const response = await api.createCollection({ name: data.name });
          const created = response.data as Collection;
          if (localId.startsWith('local-') && created?.collection_id) {
            return { oldId: localId, newId: created.collection_id, kind: 'collection' };
          }
        }
        return null;
      }
      case 'CREATE_TASK': {
        const data: any = { ...operation.data };
        const localId = String(data.task_id || '');
        const saved = await api.createTask({
          title: data.title,
          description: data.description ?? null,
          org_id: data.org_id ?? undefined,
          note_id: data.note_id ?? undefined,
          status: data.status,
          due_date: data.due_date ?? undefined,
        });
        if (localId.startsWith('local-') && saved?.task_id) {
          return { oldId: localId, newId: saved.task_id, kind: 'task' };
        }
        return null;
      }
      case 'UPDATE_TASK': {
        const data: any = operation.data;
        await api.updateTask(data.task_id, data.payload || {});
        return null;
      }
      case 'DELETE_TASK': {
        const data: any = operation.data;
        // A never-synced local task has no server record — skip the call.
        if (!String(data.task_id).startsWith('local-')) {
          await api.deleteTask(data.task_id);
        }
        return null;
      }
      case 'ADD_TASK_LINK': {
        const data: any = operation.data;
        const localLinkId = String(data.task_link_id || '');
        const saved = await api.addTaskLink(data.task_id, data.linked_task_id, data.link_type || 'RELATED');
        if (localLinkId.startsWith('local-') && saved?.task_link_id) {
          return { oldId: localLinkId, newId: saved.task_link_id, kind: 'link' };
        }
        return null;
      }
      case 'REMOVE_TASK_LINK': {
        const data: any = operation.data;
        // A link created & removed before syncing has no server record — skip.
        if (!String(data.task_link_id).startsWith('local-')) {
          await api.removeTaskLink(data.task_id, data.task_link_id);
        }
        return null;
      }
      case 'DELETE':
        if ('tab_id' in operation.data) {
          // A never-synced local tab has no server record — skip the call.
          if (String((operation.data as any).tab_id).startsWith('local-')) return null;
          await api.createOrUpdateTab(operation.data);
        }
        return null;
      case 'MOVE_TAB': {
        const moveData = operation.data as any;
        // A still-local id means the create was lost — recreate at the destination.
        const movedIsLocal = String(moveData.tabId).startsWith('local-');
        await api.createOrUpdateTab({
          ...(movedIsLocal ? {} : { tab_id: moveData.tabId }),
          collection_id: moveData.toCollectionId,
          sequence: moveData.newSequence,
          status: 'ACTIVE',
          title: moveData.tabData.title,
          url: moveData.tabData.url,
          favicon_url: moveData.tabData.favicon_url || 'https://www.hintword.com/favicon.ico',
        });
        return null;
      }
      case 'DELETE_TAB':
        // A never-synced local tab has no server record — skip the call.
        if (String((operation.data as any).tab_id).startsWith('local-')) return null;
        await api.createOrUpdateTab(operation.data);
        return null;
      case 'REORDER_COLLECTIONS': {
        const reorderData = operation.data as any;
        await api.reorderCollections(reorderData.collections);
        return null;
      }
      case 'REORDER_TABS': {
        const reorderTabsData = operation.data as any;
        await api.reorderTabs(reorderTabsData.collectionId, reorderTabsData.tabs);
        return null;
      }
      default:
        return null;
    }
  }, []);

  // Push phase: drain the queue in order. Returns counts for reporting.
  const pushQueue = useCallback(async (): Promise<{ pushed: number; failed: number }> => {
    const operations = [...offlineQueueManager.getOperations()];
    let pushed = 0;
    let failed = 0;

    const deepReplace = (value: any, oldId: string, newId: string): any => {
      if (value === oldId) return newId;
      if (Array.isArray(value)) return value.map(v => deepReplace(v, oldId, newId));
      if (value && typeof value === 'object') {
        const out: any = {};
        for (const key of Object.keys(value)) out[key] = deepReplace(value[key], oldId, newId);
        return out;
      }
      return value;
    };

    for (let i = 0; i < operations.length; i++) {
      const operation = operations[i];
      try {
        const remap = await pushOperation(operation);
        offlineQueueManager.removeOperation(operation.id);
        pushed++;

        if (remap) {
          if (remap.kind === 'note') {
            offlineDataManager.remapNoteId(remap.oldId, remap.newId);
          } else if (remap.kind === 'collection') {
            offlineDataManager.remapCollectionId(remap.oldId, remap.newId);
          } else if (remap.kind === 'tab') {
            offlineDataManager.remapTabId(remap.oldId, remap.newId);
          } else if (remap.kind === 'task') {
            offlineDataManager.remapTaskId(remap.oldId, remap.newId);
          } else {
            offlineDataManager.remapTaskLinkId(remap.oldId, remap.newId);
          }
          offlineQueueManager.remapId(remap.oldId, remap.newId);
          for (let j = i + 1; j < operations.length; j++) {
            operations[j] = { ...operations[j], data: deepReplace(operations[j].data, remap.oldId, remap.newId) };
          }
        }
      } catch (error) {
        console.error(`Failed to sync operation ${operation.id}:`, error);
        failed++;
        const nextRetry = operation.retryCount + 1;
        if (nextRetry >= (operation.maxRetries || 3)) {
          console.warn(`Operation ${operation.id} exceeded max retries, removing from queue`);
          offlineQueueManager.removeOperation(operation.id);
        } else {
          offlineQueueManager.updateOperationRetry(operation.id, nextRetry);
        }
      }
    }

    return { pushed, failed };
  }, [pushOperation]);

  // Manual sync — the ONLY place the network is touched. Push local changes,
  // then pull fresh server data into local storage (server wins).
  const syncNow = useCallback(async () => {
    if (isSyncing) return;

    if (!isOnlineNow()) {
      toast.error("Can't sync — you're offline");
      return;
    }

    setIsSyncing(true);
    offlineQueueManager.setProcessing(true);

    try {
      // 1. Push queued local changes.
      const { pushed, failed } = await pushQueue();

      // Reflect any id remaps from the push into the query caches.
      queryClient.setQueryData(queryKeys.notes.list(), offlineDataManager.getNotes());
      queryClient.setQueryData(queryKeys.collections.list(), offlineDataManager.getCollections());

      // 2. Pull fresh server data (server wins for pulled records).
      let pulled = true;
      try {
        const [notes, collectionsRes, tasks, folders] = await Promise.all([
          api.getNotes(),
          api.getCollections(),
          api.getTasks(true),
          api.getFolders(),
        ]);
        offlineDataManager.setNotes(notes);
        offlineDataManager.setCollections(collectionsRes.data);
        offlineDataManager.setTasks(tasks);
        offlineDataManager.setFolders(folders);
        offlineDataManager.markSeeded();
        queryClient.setQueryData(queryKeys.notes.list(), notes);
        queryClient.setQueryData(queryKeys.collections.list(), collectionsRes.data);
        queryClient.setQueryData(queryKeys.tasks.list(true), tasks);
        queryClient.setQueryData(queryKeys.tasks.list(false), tasks.filter(t => !t.is_archived));
        queryClient.setQueryData(['folders'], folders);
      } catch (pullError) {
        pulled = false;
        console.error('Failed to pull server data during sync:', pullError);
      }

      const now = new Date();
      setLastSyncTime(now);
      try {
        storage.setItem(LAST_SYNC_KEY, now.toISOString());
      } catch {}

      setSyncHistory(writeHistory({
        time: now.toISOString(),
        ok: failed === 0 && pulled,
        pushed,
        failed,
        pulled,
      }));

      if (failed > 0) {
        toast.error(`${failed} change(s) failed to sync — will retry on next sync`);
      } else if (!pulled) {
        toast.error('Pushed local changes, but failed to fetch latest data');
      } else {
        toast.success('Synced');
      }
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error(error instanceof Error ? error.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
      offlineQueueManager.setProcessing(false);
    }
  }, [isSyncing, pushQueue, queryClient]);

  // Add operation to offline queue
  const addToOfflineQueue = useCallback((
    type: OfflineOperation['type'],
    data: any,
    maxRetries: number = 3
  ): string => {
    return offlineQueueManager.addOperation({ type, data, maxRetries });
  }, []);

  // Clear offline queue
  const clearOfflineQueue = useCallback(() => {
    offlineQueueManager.clearQueue();
  }, []);

  // Clear the sync history log
  const clearSyncHistory = useCallback(() => {
    try {
      storage.removeItem(HISTORY_KEY);
    } catch {}
    setSyncHistory([]);
  }, []);

  // Get sync status
  const getSyncStatus = useCallback(() => {
    return {
      isOnline,
      isReconnecting,
      isSyncing,
      pendingOperations: queue.operations.length,
      lastSyncTime,
      canSync: isOnline && !isSyncing,
    };
  }, [isOnline, isReconnecting, isSyncing, queue.operations.length, lastSyncTime]);

  return {
    // Status
    isOnline,
    isReconnecting,
    isSyncing,
    pendingOperations: queue.operations.length,
    lastSyncTime,
    syncHistory,

    // Actions
    syncNow,
    addToOfflineQueue,
    clearOfflineQueue,
    clearSyncHistory,
    getSyncStatus,

    // Queue details
    operations: queue.operations,
  };
};
